import {
  NOTE_FOLDER_SYSTEM_DEFAULT_NAMES,
  NOTE_FOLDER_SYSTEM_LABEL_KEYS
} from 'constants/note-folder';
import { NoteFolderSystemKey } from 'enums';
import { NoteFolder, NoteListScopeParams } from 'interfaces/note.interface';

const isSystemFolderKey = (key: string | null): key is NoteFolderSystemKey =>
  Object.values<string>(NoteFolderSystemKey).includes(key ?? '');

/** Meeting Recordings sits first, above Personal notes, as on the Mac. */
const isPinnedFolder = (folder: NoteFolder): boolean =>
  folder.system_key === NoteFolderSystemKey.MEETING_RECORDINGS;

/**
 * The folders directly inside `parentId` (null is the top level), in the server's A–Z
 * order. A folder whose parent is missing from the list is treated as top level so it
 * can still be reached.
 */
export const getChildNoteFolders = (
  folders: NoteFolder[],
  parentId: string | null
): NoteFolder[] => {
  const knownIds = new Set(folders.map((folder) => folder.id));
  const children = folders.filter((folder) =>
    parentId === null
      ? folder.parent_id === null || !knownIds.has(folder.parent_id)
      : folder.parent_id === parentId
  );
  return [
    ...children.filter(isPinnedFolder),
    ...children.filter((folder) => !isPinnedFolder(folder))
  ];
};

/** Top-level folder first, the open folder last; stops on a parent loop. */
export const getNoteFolderPath = (
  folders: NoteFolder[],
  folderId: string
): NoteFolder[] => {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const path: NoteFolder[] = [];
  let current = byId.get(folderId);
  while (current !== undefined && !path.includes(current)) {
    path.unshift(current);
    current =
      current.parent_id === null ? undefined : byId.get(current.parent_id);
  }
  return path;
};

/** The translation key for an unrenamed system folder, else null (show `name`). */
export const getNoteFolderLabelKey = (folder: NoteFolder): string | null => {
  const key = folder.system_key;
  if (!isSystemFolderKey(key)) {
    return null;
  }
  return folder.name === NOTE_FOLDER_SYSTEM_DEFAULT_NAMES[key]
    ? NOTE_FOLDER_SYSTEM_LABEL_KEYS[key]
    : null;
};

/**
 * `GET /note` filters for the list on screen. Without folders (the backend isn't
 * deployed, or this page never loaded them) no filter is sent: today's flat list.
 */
export const getNoteListScopeParams = ({
  noteFolders,
  activeNoteFolderId
}: {
  noteFolders: NoteFolder[] | null;
  activeNoteFolderId: string | null;
}): NoteListScopeParams => {
  if (noteFolders === null) {
    return {};
  }
  return activeNoteFolderId === null
    ? { unfiled: true }
    : { folder_id: activeNoteFolderId };
};
